import { useState, useCallback, useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { AdminOnly } from '@/components/AdminOnly'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDistrictsWithRegionByNation } from '@/hooks/useDistrictsWithRegionByNation'
import { useCohortsByNation } from '@/hooks/useCohortsByNation'
import { bulkCreateCohorts, deleteCohortsByDistrict } from '@/services/cohorts'
import { generateCohortsForDistrict } from '@/lib/cohortGenerator'

interface BulkGenerateCohortsButtonProps {
  nationId: string
  nationName: string
}

interface ProgressState {
  current: number
  total: number
  status: 'idle' | 'processing' | 'done' | 'error'
}

export function BulkGenerateCohortsButton({
  nationId,
  nationName,
}: BulkGenerateCohortsButtonProps) {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [progress, setProgress] = useState<ProgressState>({
    current: 0,
    total: 0,
    status: 'idle',
  })
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [totalCohortsGenerated, setTotalCohortsGenerated] = useState(0)

  const { districts, isLoading: districtsLoading } = useDistrictsWithRegionByNation(nationId)
  const { cohorts, isLoading: cohortsLoading } = useCohortsByNation(nationId)

  const isLoading = districtsLoading || cohortsLoading
  const districtCount = districts?.length ?? 0

  // Count districts that already have cohorts
  const districtsWithCohorts = useMemo(
    () => new Set(cohorts?.map((c) => c.district_id) ?? []),
    [cohorts]
  )
  const districtsWithCohortsCount = districtsWithCohorts.size

  const handleGenerate = useCallback(async () => {
    if (!districts || districts.length === 0) return

    setProgress({ current: 0, total: districts.length, status: 'processing' })
    setError(null)
    setTotalCohortsGenerated(0)

    let totalCohorts = 0

    try {
      for (let i = 0; i < districts.length; i++) {
        const district = districts[i]

        // Delete existing cohorts for this district
        if (districtsWithCohorts.has(district.id)) {
          await deleteCohortsByDistrict(district.id)
        }

        // Generate new cohorts
        const generatedCohorts = generateCohortsForDistrict({
          districtId: district.id,
          districtPopulation: district.population,
          settlementType: district.settlement_type,
          regionType: district.region.region_type,
        })

        // Bulk insert the cohorts
        await bulkCreateCohorts(generatedCohorts)
        totalCohorts += generatedCohorts.length

        setProgress({ current: i + 1, total: districts.length, status: 'processing' })
      }

      setProgress({ current: districts.length, total: districts.length, status: 'done' })
      setTotalCohortsGenerated(totalCohorts)

      // Invalidate all cohort queries
      await queryClient.invalidateQueries({ queryKey: ['cohorts'] })

      setSuccessMessage(`Generated ${totalCohorts} cohorts across ${districts.length} districts`)
      setIsDialogOpen(false)
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      setProgress((prev) => ({ ...prev, status: 'error' }))
      setError(err instanceof Error ? err.message : 'Failed to generate cohorts')
      // Still invalidate to show partial progress
      await queryClient.invalidateQueries({ queryKey: ['cohorts'] })
    }
  }, [districts, districtsWithCohorts, queryClient])

  const handleCancel = () => {
    if (progress.status !== 'processing') {
      setIsDialogOpen(false)
      setError(null)
      setProgress({ current: 0, total: 0, status: 'idle' })
    }
  }

  const progressPercent =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0

  return (
    <AdminOnly>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setIsDialogOpen(true)} disabled={isLoading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Generate All Cohorts
        </Button>

        {successMessage && <span className="text-sm text-green-600">{successMessage}</span>}

        <AlertDialog open={isDialogOpen} onOpenChange={(open) => !open && handleCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate All Cohorts</AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                {progress.status === 'idle' && (
                  <>
                    <span className="block">
                      Generate cohorts for all {districtCount} districts in "{nationName}"?
                    </span>
                    {districtsWithCohortsCount > 0 && (
                      <span className="block font-medium text-amber-600">
                        {districtsWithCohortsCount}{' '}
                        {districtsWithCohortsCount === 1 ? 'district already has' : 'districts already have'}{' '}
                        cohorts that will be replaced.
                      </span>
                    )}
                  </>
                )}
                {progress.status === 'processing' && (
                  <div className="space-y-2">
                    <span className="block">Generating cohorts...</span>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <span className="text-muted-foreground block text-center text-sm">
                      {progress.current} of {progress.total} districts
                    </span>
                  </div>
                )}
                {progress.status === 'done' && (
                  <span className="block text-green-600">
                    Successfully generated {totalCohortsGenerated} cohorts across{' '}
                    {progress.total} districts!
                  </span>
                )}
                {error && <span className="block text-red-600">{error}</span>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {progress.status === 'idle' && (
                <>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleGenerate} disabled={districtCount === 0}>
                    Generate All
                  </AlertDialogAction>
                </>
              )}
              {progress.status === 'processing' && (
                <AlertDialogCancel disabled>Processing...</AlertDialogCancel>
              )}
              {(progress.status === 'done' || progress.status === 'error') && (
                <AlertDialogAction onClick={handleCancel}>Close</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminOnly>
  )
}
